/**
 * IndexedDB storage for large chat table data.
 * Offloads tables with many rows (>500) from Vuex to avoid reactivity overhead and improve performance.
 */

const DB_NAME = 'caseblocks-chat-tables'
const DB_VERSION = 1
const STORE_NAME = 'tables'

let dbPromise = null

function getDb () {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
      request.onupgradeneeded = (e) => {
        const db = e.target.result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' })
        }
      }
    })
  }
  return dbPromise
}

/**
 * Store table data in IndexedDB. Returns a reference object to use in place of the data.
 * @param {string} messageId - Unique message ID
 * @param {Array} rows - Full table data (array of rows)
 * @param {Array} headers - Pre-computed header metadata
 * @param {boolean} hasIdField - Whether rows contain id field (for case linking)
 * @returns {Object} Reference: { __indexedDb: true, key, totalRows, headers, hasIdField }
 */
export async function storeTableData (messageId, rows, headers, hasIdField = false) {
  const key = `chat-table-${messageId}`
  const db = await getDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    store.put({
      key,
      rows,
      headers,
      totalRows: rows.length,
      hasIdField,
      storedAt: Date.now()
    })
    tx.oncomplete = () => {
      resolve({
        __indexedDb: true,
        key,
        totalRows: rows.length,
        headers,
        hasIdField
      })
    }
    tx.onerror = () => reject(tx.error)
  })
}

/**
 * Fetch a page of rows from IndexedDB.
 * @param {string} key - Storage key
 * @param {number} offset - Start index
 * @param {number} limit - Number of rows to fetch
 * @returns {Promise<Array>} Rows for the requested page
 */
export async function getTablePage (key, offset, limit) {
  const db = await getDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const request = store.get(key)
    request.onsuccess = () => {
      const record = request.result
      if (!record || !record.rows) {
        resolve([])
        return
      }
      const page = record.rows.slice(offset, offset + limit)
      resolve(page)
    }
    request.onerror = () => reject(request.error)
  })
}

/**
 * Fetch all rows from IndexedDB (for export/download).
 * @param {string} key - Storage key
 * @returns {Promise<Array>} Full rows array
 */
export async function getAllTableRows (key) {
  const db = await getDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const request = store.get(key)
    request.onsuccess = () => {
      const record = request.result
      resolve((record && record.rows) || [])
    }
    request.onerror = () => reject(request.error)
  })
}

/**
 * Remove table data from IndexedDB (cleanup when conversation is cleared).
 * @param {string} key - Storage key
 */
export async function removeTableData (key) {
  const db = await getDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    store.delete(key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

